import { isEqual, omit } from "lodash";
import {
  ConsultantPipelineCardFragment,
  GetConsultantPipelineBoardQuery
} from "@graphql";
import { ConsultantFilterState } from "@consultants/consultants-search-new/utils/consultant-filter-state";
import { BoardDescription } from "../kanban-board/kanban-types";
import { mapDataToBoard } from "../kanban-board/map-data-to-board";
import { moveCard } from "../kanban-board/move-card";
import { deleteCard } from "../kanban-board/delete-card";
import { applyFilter } from "./consultant-pipeline-board-filters";
import { defaultFilter } from "./use-consultant-pipeline-board-context";
import {
  applyCardsToBoard,
  removeCardFromBoard,
  sortBoard,
  upsertCardInBoard
} from "./utils";

type InitializeBoardAction = {
  type: "INITIALIZE_BOARD";
  payload: {
    response: GetConsultantPipelineBoardQuery;
  };
};

type LoadCardsAction = {
  type: "LOAD_CARDS_ACTION";
  payload: {
    cards: ConsultantPipelineCardFragment[];
  };
};

type RefetchBoardAction = {
  type: "REFETCH_BOARD";
  payload: {
    response: GetConsultantPipelineBoardQuery;
  };
};

type MoveCardAction = {
  type: "MOVE_CARD";
  payload: {
    cardId: string;
    destColumnId: string;
    destPosition: number;
  };
};

type RemoveCardAction = {
  type: "REMOVE_CARD";
  payload: {
    cardId: string;
  };
};

type UpsertCardAction = {
  type: "UPSERT_CARD";
  payload: {
    card: ConsultantPipelineCardFragment;
  };
};

type RemoveCardByIdAction = {
  type: "REMOVE_CARD_BY_ID";
  payload: {
    cardId: string;
  };
};

type FilterChangeAction = {
  type: "FILTER_CHANGE";
  payload: {
    filter: ConsultantFilterState;
  };
};

type ChangeBoardUserAction = {
  type: "CHANGE_BOARD_USER";
  payload: {
    userId: string;
    filter: ConsultantFilterState;
  };
};

type ChangeUntagConsultantIdAction = {
  type: "CHANGE_UNTAG_CONSULTANT_ID";
  payload: {
    consultantId: string;
    questionText: string;
  };
};

type ChangeConsultantRelevantLater = {
  type: "CHANGE_CONSULTANT_RELEVANT_LATER";
  payload: {
    consultantId: string;
  };
};

type ClearCardNextContactDateAction = {
  type: "CLEAR_CARD_NEXT_CONTACT_DATE";
  payload: {
    cardId: string;
  };
};

export type BoardAction =
  | InitializeBoardAction
  | LoadCardsAction
  | RefetchBoardAction
  | MoveCardAction
  | RemoveCardAction
  | UpsertCardAction
  | RemoveCardByIdAction
  | FilterChangeAction
  | ChangeBoardUserAction
  | ChangeUntagConsultantIdAction
  | ChangeConsultantRelevantLater
  | ClearCardNextContactDateAction;

export type BoardState = {
  board: BoardDescription<ConsultantPipelineCardFragment>;
  initialBoard: BoardDescription<ConsultantPipelineCardFragment>;
  lastActionType: string | null;
  filters: ConsultantFilterState;
  filterIsDirty: boolean;
  boardUserId: string;
  untagConsultantId: string;
  untagConsultantQuestionText: string;
  setConsultantRelevantLaterId: string | null;
  // Cards loaded before the board structure are buffered here and drained on
  // INITIALIZE_BOARD/REFETCH_BOARD so the two queries can run in parallel.
  pendingCards: ConsultantPipelineCardFragment[] | null;
};

export function consultantPipelineBoardReducer(state: BoardState, action: BoardAction) {
  if (action.type === "INITIALIZE_BOARD" || action.type === "REFETCH_BOARD") {
    if (!action.payload.response.kanbanBoard) {
      return {
        ...state,
        board: null,
        initialBoard: null
      };
    }

    let initalBoard = mapDataToBoard(action.payload.response.kanbanBoard);

    // Carry forward cards from the previous board into the new column
    // structure when we're refetching the *same* board. Without this, any
    // refetch() of the board query (e.g. dialog onClose handlers) wipes
    // every card until the next cards-query response arrives — the symptom
    // is "card disappears from source and never appears in destination".
    // When the user switches to a different board (id changes) we still
    // start from empty columns so we don't briefly show another user's
    // cards.
    const isSameBoard =
      !!state.initialBoard && state.initialBoard.id === initalBoard.id;
    if (isSameBoard) {
      const existingByColumn = new Map<
        string | number,
        typeof initalBoard.columns[0]["cards"]
      >();
      for (const col of state.initialBoard.columns) {
        existingByColumn.set(col.id, col.cards);
      }
      for (const col of initalBoard.columns) {
        const existing = existingByColumn.get(col.id);
        if (existing) col.cards = existing;
      }
    }

    // If cards arrived before the board structure, apply them now.
    if (state.pendingCards) {
      initalBoard = applyCardsToBoard(state.pendingCards, initalBoard);
    }
    initalBoard = sortBoard(initalBoard);
    const newBoardState = applyFilter(state.filters, initalBoard);

    return {
      ...state,
      board: newBoardState,
      initialBoard: initalBoard,
      pendingCards: null,
      lastActionType: action.type
    };
  } else if (action.type === "LOAD_CARDS_ACTION") {
    if (!state.board) {
      // Board structure hasn't arrived yet — buffer until INITIALIZE_BOARD.
      return { ...state, pendingCards: action.payload.cards };
    }
    let board = applyCardsToBoard(action.payload.cards, state.board);
    board = sortBoard(board);

    return { ...state, board };
  } else if (action.type === "MOVE_CARD") {
    const newBoardState = moveCard(
      state.initialBoard,
      action.payload.cardId,
      action.payload.destColumnId,
      action.payload.destPosition
    );

    return {
      ...state,
      board: applyFilter(state.filters, newBoardState),
      initialBoard: newBoardState,
      lastActionType: action.type
    };
  } else if (action.type === "REMOVE_CARD") {
    const newBoardState = deleteCard(state.initialBoard, action.payload.cardId);

    return {
      ...state,
      board: applyFilter(state.filters, newBoardState),
      initialBoard: newBoardState,
      lastActionType: action.type
    };
  } else if (action.type === "CLEAR_CARD_NEXT_CONTACT_DATE") {
    if (!state.initialBoard) {
      return state;
    }
    const { cardId } = action.payload;
    // Find the card and clear consultant.nextContactDate in place. Per
    // CRMDEV-2644 the card colour and the "Nästa kontakt" label are driven
    // by consultant.nextContactDate (irrespective of column), so after
    // moving a card out of "Aktuell längre fram" the local cache has to
    // forget the previous next-contact-date or the card stays yellow with
    // the old date visible until the originator-skip window expires and
    // a subscription patch refreshes the data. We mutate `card.issue` in
    // place so react-beautiful-dnd's draggable identity is preserved
    // (same pattern used by upsertCardInBoard); the column arrays are
    // rebuilt with new references so the reducer returns a fresh state.
    let found = false;
    for (const col of state.initialBoard.columns) {
      const card = col.cards.find((c) => c.id === cardId);
      if (!card) continue;
      if (!card.issue.consultant?.nextContactDate) {
        // already cleared
        return state;
      }
      card.issue = {
        ...card.issue,
        consultant: {
          ...card.issue.consultant,
          nextContactDate: null
        }
      };
      found = true;
      break;
    }
    if (!found) return state;
    const newBoardState = {
      ...state.initialBoard,
      columns: state.initialBoard.columns.map((col) => ({
        ...col,
        cards: [...col.cards]
      }))
    };
    return {
      ...state,
      board: applyFilter(state.filters, newBoardState),
      initialBoard: newBoardState,
      lastActionType: action.type
    };
  } else if (action.type === "UPSERT_CARD") {
    if (!state.initialBoard) {
      return state;
    }
    const incoming = action.payload.card;

    // Short-circuit when the patch carries data identical to what's already
    // in state. Without this, every subscription event (including the
    // redundant notifyOnConsultantUpdated republish after our own moveCard)
    // rebuilds the columns array, which makes react-beautiful-dnd re-mount
    // the draggable. The result is a 1-2s visual "card disappears and
    // reappears" flicker even when nothing actually changed.
    //
    // We deep-compare the full incoming card against the cached issue so
    // we don't silently swallow remote edits to unrelated fields (status
    // note, consultant data, etc.) when columnId+order happen to match.
    // See CRMDEV-6787 follow-up.
    for (const col of state.initialBoard.columns) {
      const existing = col.cards.find((c) => c.id === incoming._id);
      if (!existing) continue;
      if (col.id === incoming.columnId && isEqual(existing.issue, incoming)) {
        return state;
      }
      break;
    }

    const newBoardState = sortBoard(
      upsertCardInBoard(incoming, state.initialBoard)
    );
    return {
      ...state,
      board: applyFilter(state.filters, newBoardState),
      initialBoard: newBoardState,
      lastActionType: action.type
    };
  } else if (action.type === "REMOVE_CARD_BY_ID") {
    if (!state.initialBoard) {
      return state;
    }
    const newBoardState = removeCardFromBoard(
      action.payload.cardId,
      state.initialBoard
    );
    return {
      ...state,
      board: applyFilter(state.filters, newBoardState),
      initialBoard: newBoardState,
      lastActionType: action.type
    };
  } else if (action.type === "FILTER_CHANGE") {
    const omitFields = ["sort", "text"];

    return {
      ...state,
      filters: action.payload.filter,
      filterIsDirty: !isEqual(
        omit(defaultFilter, omitFields),
        omit(action.payload.filter, omitFields)
      ),
      board: applyFilter(action.payload.filter, state.initialBoard),
      lastActionType: action.type
    };
  } else if (action.type === "CHANGE_BOARD_USER") {
    return {
      ...state,
      boardUserId: action.payload.userId,
      filters: action.payload.filter,
      filterIsDirty: false
    };
  } else if (action.type === "CHANGE_UNTAG_CONSULTANT_ID") {
    return {
      ...state,
      untagConsultantId: action.payload.consultantId,
      untagConsultantQuestionText: action.payload.questionText,
      setConsultantRelevantLaterId: null
    };
  } else if (action.type === "CHANGE_CONSULTANT_RELEVANT_LATER") {
    return {
      ...state,
      untagConsultantId: null,
      untagConsultantQuestionText: "",
      setConsultantRelevantLaterId: action.payload.consultantId
    };
  } else {
    return state;
  }
}
